import * as React from 'react';

type MonthOption = {label: string; index: number};

type UseMonthComboboxParams = {
  monthLabels: string[];
  monthIndex: number | null;
  isFieldFocused: boolean;
  disabled: boolean;
  onSelect: (index: number) => void;
  onCommit: (index: number) => void;
  onClear: () => void;
};

function useMonthCombobox({
  monthLabels,
  monthIndex,
  isFieldFocused,
  disabled,
  onSelect,
  onCommit,
  onClear,
}: UseMonthComboboxParams) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [highlightIndex, setHighlightIndex] = React.useState(-1);

  const labelFor = React.useCallback(
    (index: number | null) => (index !== null ? (monthLabels[index] ?? '') : ''),
    [monthLabels],
  );

  React.useEffect(() => {
    if (isFieldFocused) return;
    setInputValue(labelFor(monthIndex));
  }, [monthIndex, labelFor, isFieldFocused]);

  const filteredMonths = React.useMemo<MonthOption[]>(() => {
    const all = monthLabels.map((label, index) => ({label, index}));
    const query = inputValue.trim().toLowerCase();
    if (!query || inputValue === labelFor(monthIndex)) return all;
    return all.filter(option => option.label.toLowerCase().includes(query));
  }, [inputValue, monthIndex, monthLabels, labelFor]);

  React.useEffect(() => {
    if (!isOpen) {
      setHighlightIndex(-1);
      return;
    }
    if (monthIndex !== null) {
      const index = filteredMonths.findIndex(option => option.index === monthIndex);
      if (index >= 0) {
        setHighlightIndex(index);
        return;
      }
    }
    setHighlightIndex(filteredMonths.length > 0 ? 0 : -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filteredMonths.length]);

  const selectMonth = React.useCallback(
    (index: number) => {
      setInputValue(labelFor(index));
      setIsOpen(false);
      onSelect(index);
    },
    [labelFor, onSelect],
  );

  const commitInput = React.useCallback(() => {
    const query = inputValue.trim().toLowerCase();
    if (!query) {
      if (monthIndex !== null) onClear();
      setInputValue('');
      return;
    }
    const exactMatch = monthLabels.findIndex(label => label.toLowerCase() === query);
    if (exactMatch >= 0) {
      if (exactMatch !== monthIndex) onCommit(exactMatch);
      setInputValue(labelFor(exactMatch));
      return;
    }
    setInputValue(labelFor(monthIndex));
  }, [inputValue, monthIndex, monthLabels, labelFor, onClear, onCommit]);

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
      setIsOpen(true);
      setHighlightIndex(0);
    },
    [],
  );

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setIsOpen(true);
        setHighlightIndex(prev =>
          filteredMonths.length === 0
            ? -1
            : Math.min(prev + 1, filteredMonths.length - 1),
        );
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setIsOpen(true);
        setHighlightIndex(prev => Math.max(prev - 1, 0));
        return;
      }
      if (event.key === 'Enter') {
        if (!isOpen) return;
        event.preventDefault();
        const target = filteredMonths[highlightIndex];
        if (target) {
          selectMonth(target.index);
        } else {
          commitInput();
        }
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
        setInputValue(labelFor(monthIndex));
        return;
      }
      if (event.key === 'Tab') {
        setIsOpen(false);
      }
    },
    [
      commitInput,
      disabled,
      filteredMonths,
      highlightIndex,
      isOpen,
      labelFor,
      monthIndex,
      selectMonth,
    ],
  );

  return {
    isOpen,
    setIsOpen,
    inputValue,
    highlightIndex,
    setHighlightIndex,
    filteredMonths,
    selectMonth,
    commitInput,
    handleInputChange,
    handleInputKeyDown,
  };
}

export {useMonthCombobox};
export type {MonthOption};
